import type { UIMessage } from 'ai';

/** The question contract between the backend's `ask_user_question` tool and these cards.
 *
 * The question arrives as the tool call's input, which reaches the browser live in the stream and
 * is persisted in the message's parts, so one reader covers both. The answer arrives separately,
 * on the message's question block, which is what the backend fills when the reply comes in. */

export type QuestionKind = 'single_select' | 'multi_select' | 'approve_plan';

export interface QuestionOption {
  title: string;
  description?: string;
}

export interface QuestionAsked {
  question: string;
  helper?: string;
  options: QuestionOption[];
}

/** The tool call's input, as the model sent it. */
export interface QuestionAsk {
  kind: QuestionKind;
  questions: QuestionAsked[];
  planBody?: string;
}

/** The stored record, carrying the answer once given. Mirrors the backend's question block. */
export interface QuestionRecord {
  id: string;
  type: 'QUESTION';
  status: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  kind: 'SINGLE_SELECT' | 'MULTI_SELECT' | 'APPROVE_PLAN';
  toolCallId: string;
  questions: {
    question: string;
    helper?: string;
    options: QuestionOption[];
    selectedAnswer?: string;
    skipped?: boolean;
  }[];
  planBody?: string;
}

type MessagePart = UIMessage['parts'][number];

/** Reads the ask_user_question payload and the stored question record. Constants live here so the
 * tool name and the answer-marker part type have one definition each. */
export class QuestionBlockHelper {
  static readonly ASK_USER_QUESTION_TOOL_NAME = 'ask_user_question';

  /** Longest chosen answer the backend accepts (`ANSWER_MAX_LENGTH` on the answers DTO). Write-ins
   * are capped at the input so an over-long answer is stopped there rather than rejected on send. */
  static readonly ANSWER_MAX_LENGTH = 300;

  /** Marks a user message as a card's answer, so its bubble is left out rather than repeating the
   * recap. Attached by the sender and stored by the backend; never inferred from the text. */
  static readonly QUESTION_ANSWER_PART_TYPE = 'data-question-answer';

  private static isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }

  /** A model writing markdown into a JSON string field often escapes the backslash of its own line
   * breaks, so the plan arrives as the two characters `\` and `n` and renders as one long line. The
   * live card reads the tool call's arguments as the provider sent them, so it restores them here;
   * the stored record is normalized by the backend when it writes the block. */
  private static restoreEscapedLineBreaks(markdown: string): string {
    return markdown
      .replace(/\\r\\n/g, '\n')
      .replace(/\\n/g, '\n')
      .replace(/\\t/g, '\t');
  }

  private static readOption(value: unknown): QuestionOption | null {
    if (!QuestionBlockHelper.isRecord(value) || typeof value.title !== 'string')
      return null;

    return {
      title: value.title,
      ...(typeof value.description === 'string'
        ? { description: value.description }
        : {}),
    };
  }

  private static readQuestion(value: unknown): QuestionAsked | null {
    if (
      !QuestionBlockHelper.isRecord(value) ||
      typeof value.question !== 'string'
    )
      return null;
    if (!Array.isArray(value.options)) return null;

    const options = value.options.map(QuestionBlockHelper.readOption);
    if (options.some((option) => option === null)) return null;

    return {
      question: value.question,
      ...(typeof value.helper === 'string' ? { helper: value.helper } : {}),
      options: options as QuestionOption[],
    };
  }

  /** Tolerant on purpose: the payload is schema-validated at the source, but a card must never be the
   * reason a conversation fails to render. Anything unreadable returns null and the tool renders as
   * an ordinary tool row. */
  static parseQuestionAsk(input: unknown): QuestionAsk | null {
    if (!QuestionBlockHelper.isRecord(input)) return null;
    if (
      input.kind !== 'single_select' &&
      input.kind !== 'multi_select' &&
      input.kind !== 'approve_plan'
    )
      return null;
    if (!Array.isArray(input.questions) || input.questions.length === 0)
      return null;

    const questions = input.questions.map(QuestionBlockHelper.readQuestion);
    if (questions.some((question) => question === null)) return null;

    return {
      kind: input.kind,
      questions: questions as QuestionAsked[],
      ...(typeof input.planBody === 'string'
        ? {
            planBody: QuestionBlockHelper.restoreEscapedLineBreaks(
              input.planBody
            ),
          }
        : {}),
    };
  }

  /** Tolerant for the same reason as parseQuestionAsk: the record is read on every load of every
   * conversation, including blocks written by older backend versions. */
  static findQuestionRecord(
    blocks: unknown,
    toolCallId: string
  ): QuestionRecord | null {
    if (!Array.isArray(blocks)) return null;

    const found = blocks.find(
      (block) =>
        QuestionBlockHelper.isRecord(block) &&
        block.type === 'QUESTION' &&
        block.toolCallId === toolCallId &&
        Array.isArray(block.questions)
    );

    return (found as QuestionRecord | undefined) ?? null;
  }

  /** React keys for a list of labels that may repeat: repeats get a running suffix. */
  static uniqueKeys(labels: string[]): string[] {
    const seen = new Map<string, number>();
    return labels.map((label) => {
      const count = seen.get(label) ?? 0;
      seen.set(label, count + 1);
      return count === 0 ? label : `${label}#${count}`;
    });
  }

  /** True when this message was sent by a card. Read off the marker part, not off its text. */
  static isQuestionAnswerMessage(parts: MessagePart[]): boolean {
    return QuestionBlockHelper.answeredToolCallId(parts) !== null;
  }

  /** The call this message answers, from its marker part; null when it is not a card's answer. */
  static answeredToolCallId(parts: MessagePart[]): string | null {
    const marker = parts.find(
      (part) => part.type === QuestionBlockHelper.QUESTION_ANSWER_PART_TYPE
    );
    if (
      !marker ||
      !('data' in marker) ||
      !QuestionBlockHelper.isRecord(marker.data)
    )
      return null;
    const { toolCallId } = marker.data;
    return typeof toolCallId === 'string' ? toolCallId : null;
  }
}
